import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth, canSeeCosts, isDesigner } from '@/context/AuthContext';
import { apiFetch } from '@/lib/supabase';
import { fmtUSD, fmtPct } from '@/lib/calc';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Flower2, Plus, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ProductDetail from '@/components/catalog/ProductDetail';
import ProductCreateDialog from '@/components/catalog/ProductCreateDialog';

const WF_COLOR = {
  Active: 'bg-[hsl(var(--muted-green))] text-white border-[hsl(var(--muted-green-dark))]',
  Approved: 'bg-[hsl(var(--muted-green))]/25 text-[hsl(var(--muted-green-dark))] border-[hsl(var(--muted-green))]/50',
  Review: 'bg-[hsl(60_30%_88%)] text-foreground border-[hsl(60_20%_78%)]',
  Draft: 'bg-[hsl(var(--muted))] text-muted-foreground border-border',
  Archived: 'bg-foreground text-background border-foreground',
};

export default function Products() {
  const { role } = useAuth();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [q, setQ] = useState('');
  const [selectedSku, setSelectedSku] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [showDrafts, setShowDrafts] = useState(true);
  const [productsState, setProductsState] = useState('loading'); // loading | ok | error
  const [productsError, setProductsError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(24);

  const showPrice = ['admin','manager','sales','purchasing'].includes(role);
  const canManage = ['admin','manager'].includes(role);

  // Products load — independent of collections. Render as soon as this resolves.
  const loadProducts = useCallback(async () => {
    setProductsState('loading'); setProductsError(null);
    try {
      const p = await apiFetch('/catalog/products');
      setProducts(p || []);
      setProductsState('ok');
    } catch (e) {
      setProductsError(e.message);
      setProductsState('error');
    }
  }, []);
  // Collections load — non-blocking, best-effort. Failure is silent (filter falls back to category).
  const loadCollections = useCallback(async () => {
    try { setCollections(await apiFetch('/collections')); } catch {}
  }, []);
  useEffect(() => { loadProducts(); loadCollections(); }, [loadProducts, loadCollections]);
  const load = () => { loadProducts(); loadCollections(); };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (products || []).filter(p => {
      if (!showDrafts && p.is_draft) return false;
      if (!s) return true;
      return (p.name || '').toLowerCase().includes(s) ||
             (p.product_sku || '').toLowerCase().includes(s) ||
             (p.collection_name || '').toLowerCase().includes(s);
    });
  }, [products, q, showDrafts]);

  const visibleItems = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = filtered.length > visibleCount;

  const byCollection = useMemo(() => {
    const g = {};
    visibleItems.forEach(p => {
      const k = p.collection_name || p.category || 'Uncategorized';
      (g[k] ||= []).push(p);
    });
    return g;
  }, [visibleItems]);

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1400px]" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="velvet-eyebrow mb-3">Atelier catalogue</div>
          <h1 className="velvet-page-title">Products &amp; Recipes</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
            Every signature arrangement, versioned. Approved recipes are immutable — edits fork into a new draft.
            {!canSeeCosts(role) && ' Costs are hidden at your permission level.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search SKU, name…" className="pl-9 w-64 rounded-sm" data-testid="products-search-input" />
          </div>
          <label className="text-xs text-muted-foreground flex items-center gap-1.5 select-none">
            <input type="checkbox" checked={showDrafts} onChange={e => setShowDrafts(e.target.checked)} data-testid="show-drafts-toggle" />
            Show drafts
          </label>
          {canManage && (
            <Button onClick={() => setCreateOpen(true)} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="new-product-btn">
              <Plus className="h-4 w-4 mr-1" /> New product
            </Button>
          )}
        </div>
      </div>

      {productsState === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center" data-testid="products-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading products…
        </div>
      )}
      {productsState === 'error' && (
        <div className="velvet-card p-8 text-center max-w-md mx-auto" data-testid="products-error">
          <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-3" />
          <p className="text-sm text-muted-foreground mb-4">{productsError || 'Could not load products.'}</p>
          <Button onClick={loadProducts} className="rounded-sm" variant="outline" data-testid="products-retry">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </div>
      )}
      {productsState === 'ok' && (
        <>
          {Object.entries(byCollection).map(([coll, items]) => (
          <section key={coll} className="mb-10">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="velvet-section-title">{coll}</h2>
              <span className="velvet-eyebrow">{items.length} products</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {items.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelectedSku(p.product_sku)}
                  className="text-left velvet-card overflow-hidden group hover:-translate-y-[2px] transition-transform"
                  data-testid={`product-card-${p.product_sku}`}
                >
                  <div className="aspect-[4/3] bg-[hsl(var(--muted))] overflow-hidden relative">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; }} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground" data-testid={`placeholder-${p.product_sku}`}>
                        <Flower2 className="h-8 w-8" />
                      </div>
                    )}
                    {p.workflow_state && (
                      <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${WF_COLOR[p.workflow_state] || WF_COLOR.Draft}`}>
                        {p.workflow_state}
                      </span>
                    )}
                    {p.is_demo && <span className="absolute top-3 right-3 text-[9px] px-1.5 py-0.5 border border-border bg-card text-muted-foreground uppercase tracking-wider">DEMO</span>}
                  </div>
                  <div className="p-5">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="font-serif text-2xl leading-tight tracking-tight">{p.name || 'Untitled'}</h3>
                      {showPrice && (
                        p.current_price != null ? (
                          <span className="velvet-money text-sm whitespace-nowrap">{fmtUSD(p.current_price)}</span>
                        ) : (
                          <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground whitespace-nowrap" data-testid={`price-pending-${p.product_sku}`}>Price pending</span>
                        )
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      <span className="font-mono">{p.product_sku}</span>
                      <span>·</span>
                      <span>{p.recipe_status_text || '—'}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
          ))}
          {hasMore && (
            <div className="text-center py-6">
              <Button variant="outline" className="rounded-sm" onClick={() => setVisibleCount(c => c + 24)} data-testid="load-more-products">
                Load more ({filtered.length - visibleCount} remaining)
              </Button>
            </div>
          )}
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-16">No products match your filters.</p>
          )}
        </>
      )}

      <ProductDetail sku={selectedSku} onClose={() => setSelectedSku(null)} onChanged={load} />
      <ProductCreateDialog open={createOpen} collections={collections} onClose={() => setCreateOpen(false)} onCreated={load} />
    </div>
  );
}
